import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "components/app/dialog/dialog";
import heic2any from "heic2any";
import imageCompression from "browser-image-compression";
import { encode as encodeJPEG } from "@jsquash/jpeg";

import { ScrollArea, ScrollBar } from "components/ui/scroll-area";
import { AlignJustify, Crop, FlipHorizontal2, FlipVertical2, Maximize, PlusSquare, RotateCcw, RotateCw, Trash2, X } from "lucide-react";

import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AppLog } from "services";
import { useAppSelector } from "store/store";
import { RouteApi, StatusMsg } from "utils";
import { RequestApi, RequestApiDisableHead } from "utils/requestApi";

import { buildStyles, CircularProgressbarWithChildren } from "react-circular-progressbar";

import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import SelectGroupRadioOptionSingle from "components/app/function/SelectOneRadioOption";

import DatePicker from "components/app/function/DatePicker";
import InputSelectAutoSuggestNoneBorder from "components/app/function/InputSelectAutoSuggestNoneBorder";

import { Cropper, CropperRef } from "react-advanced-cropper";

import "react-advanced-cropper/dist/style.css";
import { cn } from "lib/utils";
import Loader2 from "components/app/loader/Loader2";

interface DialogProps {
  isDailogUploadStaff: boolean;
  setIsDailogUploadStaff: (value: boolean) => void;
  editEmloyeeId: number;
  department: any;
  setShopEmployee: any;
  setAllsetDepartment: any;
  userShopShiftGroup: any;
  editItemCategories: any;
}
type CompressedImage = {
  original: File;
  compressed: File;
  originalUrl: string;
  compressedUrl: string;
  thumbnail: File;
  thumbnailUrl: string;
  name: string;
  width?: number;
  height?: number;
  isSquare?: boolean;
  local_name?: string;
  department?: string;
};

type InitData = {
  employees: Employee[];
};

type Employee = {
  employee_id: string;
  local_name: string;
  phone_no: number;
  position: string;
  local_description: string;
  gender: string;
  joined_date: string;
  picture?: string;
  picture_src?: string;
  thumbnail?: string;
  thumbnail_src?: string;
  provider_storage_host_id?: number;
};

export default function DialogUploadStaff({
  isDailogUploadStaff,
  setIsDailogUploadStaff,
  editEmloyeeId,
  department,
  setShopEmployee,
  setAllsetDepartment,
  userShopShiftGroup,
  editItemCategories,
}: // updateImage, // Add this prop to pass the updated image back to the parent component
DialogProps) {
  const [images, setImages] = useState<CompressedImage[]>([]);
  const [isCompressing, setIsCompressing] = useState<boolean>(false);
  const [initData, setInitData] = useState<InitData>({ employees: [] });
  const [uploadingImages, setUploadingImages] = useState<any>([]);
  const { token } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const shopId = searchParams.get("id");
  const [storageId, setStorangeId] = useState();
  const [hostName, setHostName] = useState();
  const [isInit, setIsinit] = useState<boolean>(true);

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [ishiddenButton, setIshiddenButton] = useState(false);

  const [isCropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<CompressedImage | null>(null);

  const dataURLtoFile = (dataUrl: string, filename: string): File => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  };

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setIsCompressing(true);

    const compressedResults: CompressedImage[] = [];

    for (const file of files) {
      try {
        let inputFile = file;

        // Convert HEIC/HEIF to JPEG
        const isHeic = file.type === "image/heic" || file.type === "image/heif" || file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif");

        if (isHeic) {
          const convertedBlob = (await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 })) as Blob;
          inputFile = new File([convertedBlob], file.name.replace(/\.[^/.]+$/, ".jpg"), { type: "image/jpeg" });
        }

        // Convert PNG (or other formats) to JPEG with white background using canvas
        if (inputFile.type === "image/png") {
          const imageBitmap = await createImageBitmap(inputFile);
          const canvas = document.createElement("canvas");
          canvas.width = imageBitmap.width;
          canvas.height = imageBitmap.height;

          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("Canvas context is null");

          // Fill white background (since JPEG doesn't support transparency)
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(imageBitmap, 0, 0);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const encoded = await encodeJPEG(imageData, { quality: 75 });

          inputFile = new File([encoded], file.name.replace(/\.[^/.]+$/, ".jpg"), { type: "image/jpeg" });
        }

        // Create bitmap again (in case inputFile was replaced above)
        const imageBitmap = await createImageBitmap(inputFile);
        const width = imageBitmap.width;
        const height = imageBitmap.height;
        const ratio = width / height;
        const isSquare = ratio >= 0.8 && ratio <= 1.25;

        let outputFile = inputFile;

        // Compress if over 500KB
        if (inputFile.size / 1024 > 500) {
          const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 800,
            useWebWorker: true,
          };
          const compressedBlob = await imageCompression(inputFile, options);
          outputFile = new File([compressedBlob], inputFile.name.replace(/\.[^/.]+$/, ".jpg"), { type: "image/jpeg" });
        }

        const result: CompressedImage = {
          original: file,
          compressed: outputFile,
          originalUrl: URL.createObjectURL(file),
          compressedUrl: URL.createObjectURL(outputFile),
          thumbnail: outputFile,
          thumbnailUrl: URL.createObjectURL(outputFile),
          name: outputFile.name,
          width,
          height,
          isSquare,
        };

        compressedResults.push(result);
      } catch (error) {
        console.error("Error processing file:", file.name, error);
      }
    }

    setImages(compressedResults);
    setIsCompressing(false);
    getInitUploadShopEmployee(compressedResults.length);
  };

  const getInitUploadShopEmployee = async (no_of_employee: number) => {
    try {
      const url = `${RouteApi.getInitUploadShopEmployee}token=${token}`;
      const data = {
        shop_id: shopId,
        no_of_employee: no_of_employee,
      };

      const response = await RequestApi(url, { data });

      if (response.status === "success" && response?.storage_host_cluster) {
        setInitData(response); // optional
        setInitData((prev: any) => {
          if (!Array.isArray(prev.employees)) return prev;

          const updatedEmployees = prev.employees.map((emp: any, index: number) => {
            const image = images[index];

            const local_name = image?.name ? image.name.substring(0, image.name.lastIndexOf(".")) : "";

            return {
              ...emp,
              department: editItemCategories,
              local_name,
            };
          });

          return {
            ...prev,
            employees: updatedEmployees,
          };
        });

        setImages((prev) => prev.map((img) => ({ ...img, department: editItemCategories })));

        setImages((prev) => prev.map((img) => ({ ...img, local_name: img.name.substring(0, img.name.lastIndexOf(".")) })));
        setStorangeId(response?.storage_host_cluster.id);
        setHostName(response?.storage_host_cluster.host_name);
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      AppLog.show({
        message: `[getInitUploadShopEmployee]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  const handleUploadAll = async () => {
    setSubmitAttempted(true);
    let currentImageIndex = 0;
    if (!images.length) return;

    const hasEmptyName = images.some((img: any) => {
      const ratio = img.width / img.height;
      const isRatioInRange = ratio > 0.8 && ratio < 1.25;
      const isNameMissing = !img.local_name?.trim();
      const isDepartmentMissing = !img.department?.trim();
      return isRatioInRange && (isNameMissing || isDepartmentMissing);
    });
    if (hasEmptyName) {
      setIshiddenButton(false);
      AppLog.log("Please enter a name for each image.");
      return;
    }
    setIshiddenButton(true);

    const updateImageStatus = (index: number, status: "default" | "uploading" | "success" | "skipped") => {
      setImages((prev) => prev.map((img: any, i) => (i === index ? { ...img, status } : img)));
    };

    const uploadImage = async (index: number) => {
      if (index >= images.length) {
        return;
      }

      const img: any = images[index];
      const ratio = img.width / img.height;

      // Skip non-square images
      if (ratio < 0.8 || ratio > 1.25) {
        updateImageStatus(index, "skipped");
        currentImageIndex++;
        uploadImage(currentImageIndex); // Continue to next image
        return;
      }

      const formData = new FormData();

      try {
        updateImageStatus(index, "uploading");
        setUploadingImages((prev: any) => [...prev, index]);

        formData.append(`profile_picture`, img.compressed);
        formData.append(`thubmnail_picture`, img.thumbnail);

        const uploadUrl = `${hostName}/api-mobile-flipflop/v1/submit-upload-files?token=${token}&storage_host_cluster_id=${storageId}`;

        const response = await axios.post(uploadUrl, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setImages((prev) => prev.map((im, i) => (i === index ? { ...im, progress: percent } : im)));
            }
          },
        });

        const uploadedFiles = response.data.upload_files;
        if (!Array.isArray(uploadedFiles) || uploadedFiles.length !== 2) {
          throw new Error(`Expected 2 uploaded files but got ${uploadedFiles?.length}`);
        }

        const [pictureFile, thumbnailFile] = uploadedFiles;

        const employee = {
          ...initData.employees[index],
          local_name: initData.employees[index].local_name || "",
          picture: pictureFile.attachment_url,
          picture_src: pictureFile.attachment_source,
          thumbnail: thumbnailFile.attachment_url,
          thumbnail_src: thumbnailFile.attachment_source,
          provider_storage_host_id: storageId,
        };

        const updateUrl = `${hostName}/api-mobile-flipflop/v1/submit-insert-shop-employee?token=${token}`;
        const updateResponse = await RequestApiDisableHead(updateUrl, {
          data: {
            employee_id: editEmloyeeId,
            shop_id: shopId,
            employee,
          },
        });

        if (updateResponse?.data?.status === "success") {
          updateImageStatus(index, "success");
          setAllsetDepartment(updateResponse.data.user_shop_departments);
          setShopEmployee((prevItemGroups: any) => {
            const updatedData = (prevItemGroups.data || []).map((item: any) =>
              item.id === updateResponse.data.id ? { ...item, ...updateResponse.data.user_shop_employee } : item
            );
            const exists = updatedData.some((item: any) => item.id === updateResponse.data.user_shop_employee.id);
            if (!exists) {
              updatedData.push(updateResponse.data.user_shop_employee);
            }
            return {
              ...prevItemGroups,
              data: updatedData,
            };
          });
          const isLast = index === images.length - 1; // replace `images` with your actual array
          if (isLast) {
            setIsDailogUploadStaff(false);
            handleReset();
            setIshiddenButton(false);
          }
        } else {
          console.error(updateResponse, "Update API error");
          AppLog.log("Failed to update employee");
          updateImageStatus(index, "default");
        }
      } catch (error: any) {
        console.error("Upload failed for image:", index, error);
        AppLog.show({
          message: `[uploadImage]`,
          status: StatusMsg.error,
          error,
        });
        updateImageStatus(index, "default");
      } finally {
        setUploadingImages((prev: any) => prev.filter((idx: any) => idx !== index));
        currentImageIndex++;
        uploadImage(currentImageIndex); // Continue with next image
      }
    };

    uploadImage(currentImageIndex); // Start the upload chain
  };

  const handleNameChange = (index: number, value: string) => {
    setIsinit(false);
    setInitData((prev: any) => {
      if (!Array.isArray(prev.employees)) return prev;
      const updatedEmployees = prev.employees.map((emp: any, i: number) => (i === index ? { ...emp, local_name: value } : emp));
      return {
        ...prev,
        employees: updatedEmployees,
      };
    });
    setImages((prev) => prev.map((img, i) => (i === index ? { ...img, local_name: value } : img)));
  };

  const handleDepartmentsChange = (index: number, value: string) => {
    setInitData((prev: any) => {
      if (!Array.isArray(prev.employees)) return prev;
      const updatedEmployees = prev.employees.map((emp: any, i: number) => (i === index ? { ...emp, department: value } : emp));
      return {
        ...prev,
        employees: updatedEmployees,
      };
    });
    setImages((prev) => prev.map((img, i) => (i === index ? { ...img, department: value } : img)));
  };

  const handleShiftsChange = (index: number, value: string, name: string) => {
    setInitData((prev: any) => {
      if (!Array.isArray(prev.employees)) return prev;
      const updatedEmployees = prev.employees.map((emp: any, i: number) => (i === index ? { ...emp, shift_group_id: value, local_description: name } : emp));
      return {
        ...prev,
        employees: updatedEmployees,
      };
    });
  };

  const handleReset = () => {
    setImages([]);
  };

  const handleDeleteImage = (index: number) => {
    const updatedImages = [...images];
    updatedImages.splice(index, 1);
    setImages(updatedImages);
  };

  const updatedDepartments = department?.map((d: any) => ({
    value: d.description,
    label: d.description,
  }));

  const updatedShifts = userShopShiftGroup?.map((d: any) => ({
    id: d.shift_group_id,
    value: d.local_description,
    label: d.local_description,
  }));

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();

    const droppedFiles = Array.from(e.dataTransfer.files).filter((file) => file.type.startsWith("image/"));

    if (droppedFiles.length > 0) {
      const event = {
        target: { files: droppedFiles } as any,
      } as ChangeEvent<HTMLInputElement>;

      handleImageChange(event); // reuse your existing function
    }
  };

  const cropperRef = useRef<CropperRef>(null);

  const onFlip = (horizontal: boolean, vertical: boolean) => {
    const cropper = cropperRef.current;
    if (cropper) {
      cropper.flipImage(horizontal, vertical);
    }
  };

  const onRotate = (angle: number) => {
    const cropper = cropperRef.current;
    if (cropper) {
      cropper.rotateImage(angle);
    }
  };

  const onResize = (width: number, height: number, left: number, top: number) => {
    if (cropperRef.current) {
      cropperRef.current.setCoordinates({
        width: width,
        height: height,
        left: left,
        top: top,
      });
    }
  };

  useEffect(() => {
    if (!initData?.employees || images.length === 0) return;
    if (isInit) {
      setInitData((prev: any) => {
        const updatedEmployees = prev.employees.map((emp: any, index: number) => {
          const image = images[index];

          const local_name = image?.name ? image.name.substring(0, image.name.lastIndexOf(".")) : "";
          return {
            ...emp,

            local_name,
          };
        });
        return {
          ...prev,
          employees: updatedEmployees,
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]); // ✅ runs after images are set
  function generateThumbnail(dataUrl: string, maxWidth = 200): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = maxWidth / img.width;
        canvas.width = maxWidth;
        canvas.height = img.height * scale;

        const ctx: any = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        resolve(canvas.toDataURL("image/jpeg"));
      };
      img.src = dataUrl;
    });
  }

  return (
    <Dialog open={isDailogUploadStaff}>
      <DialogContent isShowBtnXClose={false} className="custom-dialog-class flex flex-col justify-between max-w-[1000px] h-[calc(100vh-80px)]">
        <DialogHeader>
          <div className="flex items-center justify-between ">
            <div className="flex flex-col gap-2">
              <DialogTitle> Upload New Staff Profile </DialogTitle>
              <DialogDescription>Select staff's picture, enter the name, choose department, click "Submit" when you finished.</DialogDescription>
            </div>
          </div>
          <div
            className="absolute flex items-center justify-center right-3 top-[12px] z-40 cursor-pointer rounded-full hover:bg-accent hover:text-accent-foreground lg:w-10 lg:h-10 md:w-10 md:h-10 w-8 h-8 border border-gray-300 "
            onClick={() => {
              setIsDailogUploadStaff(false);
              handleReset();
            }}
          >
            <X />
          </div>
        </DialogHeader>
        {/* <ScrollArea className={` w-full gap-3 h-full  ${images.length > 0 ? "pr-[10px]" : ""}`}> */}
        {isCompressing && (
          <div className={`flex h-full  w-full  justify-center items-center my-4 `}>
            {/* <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500 border-solid"></div> */}
            <Loader2 text={"Compressing images..."} />
            {/* <span className="ml-3 text-blue-600 font-medium">Compressing images...</span> */}
          </div>
        )}
        {images.length === 0 && !isCompressing && (
          <label
            htmlFor="fileInput"
            className={`border  h-full flex flex-col items-center justify-center border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer`}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <input type="file" accept="image/*" multiple id="fileInput" onChange={handleImageChange} className="hidden" />
            <div className="cursor-pointer flex flex-col items-center space-y-2">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-xl">
                <PlusSquare size={28} className="text-gray-700" />
              </div>
              <p className="text-gray-700 dark:text-gray-300">Click to add photo</p>
              <p className="text-sm text-gray-400">Or drag and drop photo file (maximum 20 photos and photo ratio 1:1)</p>
            </div>
          </label>
        )}

        <ScrollBar orientation="vertical" />
        {images.length > 0 && !isCompressing && (
          <ScrollArea className={` w-full gap-3 h-full  ${images.length > 0 ? "pr-[10px]" : ""}`}>
            {/* (maximum 20 photos and photo ratio 1:1) */}
            {images.length > 0 && !isCompressing && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-2  ">
                {images.map((img: any, idx) => (
                  <>
                    <div
                      key={idx}
                      className={` relative border rounded-lg cursor-pointer text-center ${
                        img.width / img.height < 0.8 || img.width / img.height > 1.25 ? "border-2 border-red-500" : ""
                      }`}
                    >
                      <div className="w-full aspect-square ">
                        <img src={img.compressedUrl} alt={`Compressed-${idx}`} className={`w-full h-full object-cover rounded-t-lg`} />

                        <>
                          {img.status === "uploading" ? (
                            <div className="absolute mx-auto p-1 z-2 top-2 right-2 bg-white border-2 border-gray-300 rounded-full  text-xl  flex items-center justify-center">
                              <div className="w-6 h-6">
                                <CircularProgressbarWithChildren
                                  value={img.progress || 0}
                                  styles={buildStyles({
                                    pathColor: "#3b82f6",
                                    trailColor: "#e5e7eb",
                                  })}
                                ></CircularProgressbarWithChildren>
                              </div>
                            </div>
                          ) : img.status === "success" ? (
                            <div className="bg-white absolute mx-auto p-1 z-2 top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center  text-xl">
                              <i className="fa-solid fa-circle-check text-blue-500 border-2 rounded-full border-white"></i>
                            </div>
                          ) : (
                            <>
                              {img.width / img.height < 0.8 || img.width / img.height > 1.25 ? (
                                <label
                                  onClick={() => {
                                    setImageToCrop(img);
                                    setCropModalOpen(true);
                                  }}
                                  className=" absolute z-50 opacity-80 mx-auto p-1  top-2 right-12 text-white group bg-gray-500 hover:border-3  font-semibold cursor-pointer rounded-full 
                                                                                    w-8 h-8 flex items-center justify-center text-md  border-white"
                                >
                                  <Crop />
                                </label>
                              ) : (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <label
                                      className=" absolute opacity-80 mx-auto p-1 z-auto top-2 right-12 text-white group bg-gray-500 hover:border-3  font-semibold cursor-pointer rounded-full 
                                                                                    w-8 h-8 flex items-center justify-center text-md  border-white"
                                    >
                                      <AlignJustify className="  " />
                                    </label>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-80 relative flex flex-col gap-5">
                                    <div className=" ">
                                      <SelectGroupRadioOptionSingle
                                        datas={["Male", "Female"]}
                                        value={initData.employees?.[idx]?.gender || ""}
                                        valueChange={(e: any) => {
                                          setInitData((prev: any) => {
                                            if (!Array.isArray(prev.employees)) return prev;
                                            const updatedEmployees = prev.employees.map((emp: any, i: number) => (i === idx ? { ...emp, gender: e } : emp));
                                            return {
                                              ...prev,
                                              employees: updatedEmployees,
                                            };
                                          });
                                        }}
                                      />
                                    </div>
                                    <div>
                                      <input
                                        placeholder="Phone No."
                                        className="w-full border-t-0 border-l-0 border-r-0 border-b-[1px] px-2 outline-none dark:bg-transparent py-2"
                                        type="number"
                                        value={initData.employees?.[idx]?.phone_no || ""}
                                        onChange={(e: any) => {
                                          setInitData((prev: any) => {
                                            if (!Array.isArray(prev.employees)) return prev;
                                            const updatedEmployees = prev.employees.map((emp: any, i: number) => (i === idx ? { ...emp, phone_no: e.target.value } : emp));
                                            return {
                                              ...prev,
                                              employees: updatedEmployees,
                                            };
                                          });
                                        }}
                                      />
                                    </div>
                                    <div className="">
                                      <input
                                        placeholder="Postion"
                                        className={`w-full border-t-0 border-l-0 border-r-0 border-b-[1px] px-2 outline-none dark:bg-transparent py-2  `}
                                        value={initData.employees?.[idx]?.position || ""}
                                        onChange={(e: any) => {
                                          setInitData((prev: any) => {
                                            if (!Array.isArray(prev.employees)) return prev;
                                            const updatedEmployees = prev.employees.map((emp: any, i: number) => (i === idx ? { ...emp, position: e.target.value } : emp));
                                            return {
                                              ...prev,
                                              employees: updatedEmployees,
                                            };
                                          });
                                        }}
                                        type="text"
                                      />
                                    </div>
                                    <div className="">
                                      <div className="">
                                        <DatePicker
                                          placeholder="Joined Date"
                                          className="border-t-0 border-l-0 border-r-0 border-b-[1px] px-3 outline-none py-4 h-[45px] border-gray-300 rounded-none hover:bg-none "
                                          onChange={(e: any) => {
                                            setInitData((prev: any) => {
                                              if (!Array.isArray(prev.employees)) return prev;
                                              const updatedEmployees = prev.employees.map((emp: any, i: number) => (i === idx ? { ...emp, joined_date: e } : emp));
                                              return {
                                                ...prev,
                                                employees: updatedEmployees,
                                              };
                                            });
                                          }}
                                          value={initData.employees?.[idx]?.joined_date || ""}
                                        />
                                      </div>
                                    </div>
                                    <InputSelectAutoSuggestNoneBorder
                                      value={initData.employees?.[idx]?.local_description || "Working Hours"}
                                      onChange={(e: any) => {
                                        handleShiftsChange(idx, e.id, e.value);
                                      }}
                                      datas={updatedShifts}
                                    />
                                  </PopoverContent>
                                </Popover>
                              )}

                              <label
                                onClick={() => handleDeleteImage(idx)}
                                className="absolute z-10 opacity-80 p-1 mx-auto  top-2 right-2 text-white group bg-red-500  font-semibold cursor-pointer rounded-full 
                                                                                    w-8 h-8 flex items-center justify-center text-md  "
                              >
                                <Trash2 className="" />
                              </label>
                            </>
                          )}
                        </>
                      </div>

                      <div className="px-3 my-2 flex flex-col gap-3 text-start  ">
                        <>
                          <input
                            value={img.local_name ?? img.name.substring(0, img.name.lastIndexOf("."))}
                            placeholder="Name"
                            className={` border-t-0 border-l-0 border-r-0 border-b-[1px] px-2 py-[5px] outline-none dark:bg-transparent   ${
                              !img.local_name?.trim() && submitAttempted ? "border-red-500" : ""
                            }`}
                            onChange={(e: any) => handleNameChange(idx, e.target.value)}
                            type="text"
                          />

                          <div
                            // { position: "relative", zIndex: 9999 }
                            style={img.width / img.height < 0.8 || img.width / img.height > 1.25 ? undefined : { position: "relative", zIndex: 100 }}
                          >
                            <InputSelectAutoSuggestNoneBorder
                              status={!img.department?.trim() && submitAttempted ? true : false}
                              value={editItemCategories ? editItemCategories : "Department"}
                              onChange={(e: any) => {
                                handleDepartmentsChange(idx, e.value);
                              }}
                              datas={updatedDepartments}
                            />
                          </div>
                        </>
                      </div>
                      {img.width / img.height < 0.8 || img.width / img.height > 1.25 ? (
                        <div className="flex text-gray-100 absolute z-1 top-0 left-0  w-full h-full bg-gray-500/50 rounded-lg items-center justify-center">
                          The image content is too wild and doesn’t meet our guidelines.
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                  </>
                  // </div>
                ))}
              </div>
            )}

            {/* ------------------crop------------ */}
            {isCropModalOpen && imageToCrop && (
              <div className="fixed inset-0  bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999 }}>
                <div className="bg-white dark:bg-[#020817] border-[1px] dark:border-gray-300 border-gray-200  p-4 rounded-lg min-w-[560px] h-[60vh] flex flex-col">
                  <h2 className="text-lg font-semibold mb-2 ">Crop Profile Picture</h2>

                  <div className="flex-1 relative mt-2">
                    <Cropper
                      ref={cropperRef}
                      src={imageToCrop.compressedUrl}
                      className={cn("default-cropper__cropper cropper h-[350px] bg-red-500")}
                      stencilProps={{
                        aspectRatio: 1,
                      }}
                    />
                  </div>
                  <div className="flex space-x-2  justify-center mt-6">
                    <button onClick={() => onFlip(true, false)} className="bg-blue-500 px-5 py-2 text-white rounded-md">
                      <FlipHorizontal2 />
                    </button>
                    <button onClick={() => onFlip(false, true)} className="bg-blue-500 px-5 py-2 text-white rounded-md">
                      <FlipVertical2 />
                    </button>
                    <button onClick={() => onRotate(90)} className="bg-blue-500 px-5 py-2 text-white rounded-md">
                      <RotateCcw />
                    </button>
                    <button onClick={() => onRotate(-90)} className="bg-blue-500 px-5 py-2 text-white rounded-md">
                      <RotateCw />
                    </button>

                    <button onClick={() => onResize(100000, 100000, 0, 0)} className="bg-blue-500 px-5 py-2 text-white rounded-md">
                      <Maximize />
                    </button>
                  </div>
                  <div className="my-4 flex justify-end space-x-2">
                    <button
                      onClick={() => {
                        setCropModalOpen(false);
                        setImageToCrop(null);
                      }}
                      className="px-4 py-2 bg-red-500 rounded-md text-white"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        if (cropperRef.current) {
                          const canvas = cropperRef.current.getCanvas();

                          if (canvas) {
                            const croppedDataUrl = canvas.toDataURL("image/jpeg");
                            const croppedFile = dataURLtoFile(croppedDataUrl, imageToCrop.name || "cropped.jpg");

                            const croppedWidth = canvas.width;
                            const croppedHeight = canvas.height;

                            const thumbnailUrl = await generateThumbnail(croppedDataUrl);
                            const thumbnailFile = dataURLtoFile(thumbnailUrl, "thumbnail.jpg");

                            setImages((prev) =>
                              prev.map((img) =>
                                img === imageToCrop
                                  ? {
                                      ...img,
                                      compressed: croppedFile,
                                      compressedUrl: croppedDataUrl,
                                      thumbnail: thumbnailFile,
                                      thumbnailUrl: thumbnailUrl,
                                      width: croppedWidth,
                                      height: croppedHeight,
                                    }
                                  : img
                              )
                            );

                            setCropModalOpen(false);
                            setImageToCrop(null);
                          }
                        }
                      }}
                      className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                    >
                      Crop
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* Button */}
          </ScrollArea>
        )}
        <DialogFooter>
          {images.length > 0 && !ishiddenButton && (
            <button onClick={handleUploadAll} className="mt-6 w-full py-2 rounded-md text-white bg-blue-500 hover:bg-blue-600">
              Submit
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
